import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@eon/utils';
import { useFieldA11yProps } from './field';

const baseInput =
  'border-input bg-background ring-offset-background flex h-10 w-full rounded-md border px-3 py-2 text-sm ' +
  'file:border-0 file:bg-transparent file:text-sm file:font-medium ' +
  'placeholder:text-muted-foreground ' +
  'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 ' +
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Optional left adornment (icon). Renders inside an absolutely-positioned slot. */
  leftIcon?: ReactNode;
  /** Optional right adornment (icon or button). */
  rightAdornment?: ReactNode;
  /** Visual size — affects height and padding. */
  size?: 'sm' | 'md';
}

/**
 * TextField is the standard text input. When rendered inside a <Field>, it
 * automatically wires id / aria-* via useFieldA11yProps.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ className, type = 'text', leftIcon, rightAdornment, size = 'md', ...props }, ref) => {
    const a11y = useFieldA11yProps();
    const sizeCls = size === 'sm' ? 'h-9 text-[13px]' : 'h-10 text-sm';
    if (leftIcon || rightAdornment) {
      return (
        <div className="relative w-full">
          {leftIcon ? (
            <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center">
              {leftIcon}
            </span>
          ) : null}
          <input
            ref={ref}
            type={type}
            {...a11y}
            {...props}
            className={cn(
              baseInput,
              sizeCls,
              leftIcon ? 'pl-9' : '',
              rightAdornment ? 'pr-9' : '',
              className,
            )}
          />
          {rightAdornment ? (
            <span className="text-muted-foreground absolute right-2 top-1/2 flex -translate-y-1/2 items-center">
              {rightAdornment}
            </span>
          ) : null}
        </div>
      );
    }
    return (
      <input
        ref={ref}
        type={type}
        {...a11y}
        {...props}
        className={cn(baseInput, sizeCls, className)}
      />
    );
  },
);
TextField.displayName = 'TextField';

export interface NumberFieldProps extends Omit<TextFieldProps, 'type' | 'value' | 'onChange'> {
  value: number | '' | null | undefined;
  onChange: (value: number | '') => void;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * NumberField is a TextField specialised for numeric input. Unlike a raw
 * `<input type="number">` it keeps an empty string visible (so the user can
 * clear the field while typing) and bubbles either a `number` or the empty
 * string back to onChange. The value is automatically clamped to [min, max]
 * on commit when both are provided.
 */
export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  ({ value, onChange, min, max, step = 1, ...props }, ref) => {
    return (
      <TextField
        ref={ref}
        type="number"
        inputMode="numeric"
        value={value ?? ''}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange('');
            return;
          }
          const n = Number(raw);
          if (Number.isNaN(n)) return;
          onChange(n);
        }}
        onBlur={(e) => {
          const raw = e.target.value;
          if (raw === '') return;
          let n = Number(raw);
          if (typeof min === 'number' && n < min) n = min;
          if (typeof max === 'number' && n > max) n = max;
          onChange(n);
        }}
        {...props}
      />
    );
  },
);
NumberField.displayName = 'NumberField';

export interface TextAreaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  ({ className, rows = 3, ...props }, ref) => {
    const a11y = useFieldA11yProps();
    return (
      <textarea
        ref={ref}
        rows={rows}
        {...a11y}
        {...props}
        className={cn(baseInput, 'min-h-20 py-2', className)}
      />
    );
  },
);
TextAreaField.displayName = 'TextAreaField';
